import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid'
import { IconButton, Typography, useTheme, GlobalStyles } from '@mui/material'
import { styled } from '@mui/system'
import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { families, invitesApi } from '@api/swagger'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '@hooks/useLoadingIds'
import { useShowOnDesktop } from '@hooks/useShowOnDesktop'
import {
  CommunityInviteItemStatusEnum,
  FetchCommunityInvitesStatusEnum,
} from '@swagger'

import { useCommunitiesContext } from '../Context/CommunitiesContext'
import EmptyPage from '../Elements/EmptyPage'
import DropDown, { DropDownVariant, MenuOption } from '../Menus/DropDown'
import CommunityInviteConfirmationModal, {
  EnrollmentInviteConfirmationModalVariant,
} from '../Modals/CommunityInviteConfirmationModal'
import { useAuth } from '../Routes/AuthProvider'
import SearchBar from '../Search/SearchBar'
import { ActionableTable } from '../Table/ActionableTable'
import { Action } from '../Table/RowActions'

import {
  COMMUNITY_INVITE_EXPAND_COLUMN_WIDTH,
  CommunityInviteRow,
  getCommunityInvitesTableColumns,
  getCommunityInviteStatusLabel,
} from './getCommunityInvitesTableColumns'

import CommunityInviteExpandableRow, {
  PROGRAM_DETAIL_ROW_HEIGHT,
} from './CommunityInviteExpandableRow'

import { ALL_STATUSES, useCommunityInvites } from './useCommunityInvites'

const SearchAndFilterWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}))

const FilterContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(2),
  },
}))

const SortContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    margin: theme.spacing(0, 3),
  },
}))

export const CommunityInvitesTable: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { handleError, handleMessage } = useAppSnackbar()
  const { permissionAbility } = useAuth()
  const showOnDesktop = useShowOnDesktop()
  const navigate = useNavigate()

  const navigateToFamilyProfile = (userId: number) => {
    navigate(
      {
        pathname: `/family-profile/${userId}`,
      },
      {}
    )
  }

  const handleCellClick = (
    enrollmentInvite: CommunityInviteRow,
    field: string
  ) => {
    if (field !== 'actions' && field !== 'expand') {
      !!enrollmentInvite.parentUserKey &&
        enrollmentInvite.status === CommunityInviteItemStatusEnum.Complete &&
        navigateToFamilyProfile(enrollmentInvite.parentUserKey)
    }
  }

  const { selectedCommunityKey, academicYear } = useCommunitiesContext()

  const {
    CommunityInvitesTable: {
      fetchCommunityInvites: fetchCommunityInvitesLoadingId,
      resendInvitation: resendInvitationLoadingId,
      cancelInvitation: cancelInvitationLoadingId,
      declineInvitation: declineInvitationLoadingId,
    },
  } = useLoadingIds()

  const [searchParams] = useSearchParams()
  const initialStatusFromUrl = searchParams.get(
    'status'
  ) as FetchCommunityInvitesStatusEnum

  const {
    invites,
    search,
    statusFilter,
    hasFetchedOnce,
    hasFetchError,
    isFetchingInvites,
    pagination,
    handleSearch,
    handleStatusSelection,
    handlePaginationChange,
    triggerFetch,
  } = useCommunityInvites({
    selectedCommunityKey,
    academicYear,
    fetchCommunityInvitesLoadingId,
    initialStatusFilter: Object.values(
      FetchCommunityInvitesStatusEnum
    ).includes(initialStatusFromUrl)
      ? initialStatusFromUrl
      : undefined,
  })

  const [expandedInviteIds, setExpandedInviteIds] = useState<Set<GridRowId>>(
    new Set()
  )

  useEffect(() => {
    if (isFetchingInvites) {
      setExpandedInviteIds(new Set())
    }
  }, [isFetchingInvites])

  const [modalAction, setModalAction] = useState({
    isOpen: false,
    variant: EnrollmentInviteConfirmationModalVariant.Resend,
    inviteUuid: '',
    familyName: '',
    initialEmail: '',
  })

  const handleToggleInviteExpansion = useCallback((inviteId: GridRowId) => {
    setExpandedInviteIds((currentExpandedIds) => {
      const nextExpandedIds = new Set(currentExpandedIds)

      if (nextExpandedIds.has(inviteId)) {
        nextExpandedIds.delete(inviteId)
      } else {
        nextExpandedIds.add(inviteId)
      }

      return nextExpandedIds
    })
  }, [])

  const expandableRowOptions = useMemo(
    () => ({
      isExpanded: (row: CommunityInviteRow) => expandedInviteIds.has(row.id),
      getExpandedContentHeight: (row: CommunityInviteRow) =>
        row.programs.length * PROGRAM_DETAIL_ROW_HEIGHT,
      renderExpandedContent: (row: CommunityInviteRow) => (
        <CommunityInviteExpandableRow row={row} />
      ),
    }),
    [expandedInviteIds]
  )

  const handleConfirmationModalClose = () => {
    setModalAction((prev) => ({ ...prev, isOpen: false }))
  }

  const handleEditAndResend = (row?: CommunityInviteRow) => {
    if (!row) return
    setModalAction({
      isOpen: true,
      variant: EnrollmentInviteConfirmationModalVariant.Resend,
      inviteUuid: row.id,
      familyName: row.familyName,
      initialEmail: row.sentToEmail,
    })
  }

  const handleDecline = (row?: CommunityInviteRow) => {
    if (!row) return
    setModalAction({
      isOpen: true,
      variant: EnrollmentInviteConfirmationModalVariant.Decline,
      inviteUuid: row.id,
      familyName: row.familyName,
      initialEmail: '',
    })
  }

  const handleCancel = (row?: CommunityInviteRow) => {
    if (!row) return
    setModalAction({
      isOpen: true,
      variant: EnrollmentInviteConfirmationModalVariant.Cancel,
      inviteUuid: row.id,
      familyName: row.familyName,
      initialEmail: '',
    })
  }

  const handleResendInvitation = async (email: string) => {
    try {
      await families.resendInvitation({
        enrollmentInviteUuid: modalAction.inviteUuid,
        body: modalAction.initialEmail === email ? {} : { sentToEmail: email },
      })

      handleMessage(
        t('CommunityInviteTable.ResendInvitation', 'Invitation resent')
      )

      triggerFetch()
    } catch (error) {
      await handleError(error)
    } finally {
      setModalAction((prev) => ({ ...prev, isOpen: false }))
    }
  }

  const handleCancelInvitation = async () => {
    try {
      await families.cancelInvitation({
        uuid: modalAction.inviteUuid,
      })

      handleMessage(
        t('CommunityInviteTable.CancelInvitation', 'Invitation cancelled')
      )

      triggerFetch()
    } catch (error) {
      await handleError(error)
    } finally {
      setModalAction((prev) => ({ ...prev, isOpen: false }))
    }
  }

  const handleDeclineInvitation = async () => {
    try {
      await invitesApi.declineInvitation({
        uuid: modalAction.inviteUuid,
      })

      handleMessage(
        t('CommunityInviteTable.DeclineInvitation', 'Invitation declined')
      )

      triggerFetch()
    } catch (error) {
      await handleError(error)
    } finally {
      setModalAction((prev) => ({ ...prev, isOpen: false }))
    }
  }

  const { triggerFetch: triggerResendInvitation } = useLoadingContext({
    asyncFunction: handleResendInvitation,
    loadingId: resendInvitationLoadingId,
  })

  const { triggerFetch: triggerCancelInvitation } = useLoadingContext({
    asyncFunction: handleCancelInvitation,
    loadingId: cancelInvitationLoadingId,
  })

  const { triggerFetch: triggerDeclineInvitation } = useLoadingContext({
    asyncFunction: handleDeclineInvitation,
    loadingId: declineInvitationLoadingId,
  })

  const statusMenuOptions: MenuOption[] = [
    {
      id: ALL_STATUSES,
      name: getCommunityInviteStatusLabel(t),
    },
    {
      id: FetchCommunityInvitesStatusEnum.Complete,
      name: getCommunityInviteStatusLabel(
        t,
        FetchCommunityInvitesStatusEnum.Complete
      ),
    },
    {
      id: FetchCommunityInvitesStatusEnum.InProgress,
      name: getCommunityInviteStatusLabel(
        t,
        FetchCommunityInvitesStatusEnum.InProgress
      ),
    },
    {
      id: FetchCommunityInvitesStatusEnum.Declined,
      name: getCommunityInviteStatusLabel(
        t,
        FetchCommunityInvitesStatusEnum.Declined
      ),
    },
    {
      id: FetchCommunityInvitesStatusEnum.Cancelled,
      name: getCommunityInviteStatusLabel(
        t,
        FetchCommunityInvitesStatusEnum.Cancelled
      ),
    },
  ]

  const totalOfferedSpots = useMemo(
    () => invites.reduce((acc, curr) => acc + (curr.offeredSpots ?? 0), 0),
    [invites]
  )

  const totalSkippedSpots = useMemo(
    () => invites.reduce((acc, curr) => acc + (curr.skippedSpots ?? 0), 0),
    [invites]
  )

  const actionableTableColumns = [
    {
      fieldName: 'expand',
      columnHeaderName: '',
      sortable: false,
      width: COMMUNITY_INVITE_EXPAND_COLUMN_WIDTH,
      minWidth: COMMUNITY_INVITE_EXPAND_COLUMN_WIDTH,
      flex: 0,
      renderCell: (params: GridRenderCellParams<CommunityInviteRow>) => {
        const isExpanded = expandedInviteIds.has(params.row.id)

        return (
          <IconButton
            aria-label={
              isExpanded
                ? t(
                    'Communities.CommunityInvitesTable.CollapsePrograms',
                    'Collapse family programs'
                  )
                : t(
                    'Communities.CommunityInvitesTable.ExpandPrograms',
                    'Expand family programs'
                  )
            }
            aria-expanded={isExpanded}
            onClick={() => handleToggleInviteExpansion(params.row.id)}
            size="small"
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        )
      },
    },
    ...getCommunityInvitesTableColumns(t, {
      totalOfferedSpots,
      totalSkippedSpots,
    }),
  ]

  const rowActions: Action<CommunityInviteRow>[] = [
    {
      actionName: t(
        'Community.Invite.Table.RowAction.EditAndResend',
        'Edit & Resend'
      ),
      actionKey: 'edit',
      actionFunction: handleEditAndResend,
      hide: (row) => {
        const isEditAndResend =
          row?.status === CommunityInviteItemStatusEnum.InProgress ||
          row?.status === CommunityInviteItemStatusEnum.Declined

        return !isEditAndResend
      },
    },
    {
      actionName: t('Community.Invite.Table.RowAction.Decline', 'Decline'),
      actionKey: 'decline',
      actionFunction: handleDecline,
      hide: (row) => row?.status !== CommunityInviteItemStatusEnum.InProgress,
    },
  ]

  if (permissionAbility.can('allowCancelInvite', 'Family')) {
    rowActions.push({
      actionName: t('Community.Invite.Table.RowAction.Cancel', 'Cancel'),
      actionKey: 'cancel',
      actionFunction: handleCancel,
      hide: (row) => row?.status === CommunityInviteItemStatusEnum.Cancelled,
    })
  }
  // TODO: Move initial loading state handling into useLoadingContext
  // to avoid duplicating the hasFetchedOnce pattern across tables.
  const isLoading = !hasFetchedOnce || isFetchingInvites
  const hasActiveFilters = !!search.length || !!statusFilter

  const mobileFullWidthFormControlProps = {
    [theme.breakpoints.down('sm')]: { width: '100%' },
  }

  return (
    <section aria-label="communityInvites">
      <GlobalStyles
        styles={{
          '.MuiDataGrid-cell-pointer': {
            cursor: 'pointer',
          },
        }}
      />
      <SearchAndFilterWrapper aria-label="searchAndFilter">
        <SearchBar
          handleSearch={handleSearch}
          searchDebounce
          placeholder={t(
            'Communities.CommunityInvitesTable.Search.Placeholder',
            'Search'
          )}
        />

        <FilterContainer>
          <SortContainer>
            <DropDown
              id="statusFilter"
              menuOptions={statusMenuOptions}
              defaultValue={getCommunityInviteStatusLabel(t, statusFilter)}
              value={getCommunityInviteStatusLabel(t, statusFilter)}
              handleSelection={handleStatusSelection}
              variant={DropDownVariant.SortAndFilter}
              fullWidth={!showOnDesktop}
              formControlProps={mobileFullWidthFormControlProps}
            />
          </SortContainer>
        </FilterContainer>
      </SearchAndFilterWrapper>

      <CommunityInviteConfirmationModal
        isOpen={modalAction.isOpen}
        variant={modalAction.variant}
        familyNameActingOn={modalAction.familyName}
        initialEmail={modalAction.initialEmail}
        handleConfirmationCancel={handleConfirmationModalClose}
        resendInvitationLoadingId={resendInvitationLoadingId}
        cancelInvitationLoadingId={cancelInvitationLoadingId}
        declineInvitationLoadingId={declineInvitationLoadingId}
        handleResendInvitation={triggerResendInvitation}
        handleCancelInvitation={triggerCancelInvitation}
        handleDeclineInvitation={triggerDeclineInvitation}
      />

      {hasFetchError && !isLoading ? (
        <EmptyPage
          message={
            <Typography variant="subtitle1" component="p">
              {t(
                'Communities.CommunityInvitesTable.FetchError',
                'Unable to load community invitations. Please try again.'
              )}
            </Typography>
          }
        />
      ) : (
        <ActionableTable
          isLoading={isLoading}
          columns={actionableTableColumns}
          rows={invites}
          rowActions={rowActions}
          rowHeight={70}
          expandableRowOptions={expandableRowOptions}
          customCssTable={{
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '.MuiDataGrid-columnSeparator': {
              opacity: 0,
            },
            [theme.breakpoints.down('sm')]: {
              '.MuiDataGrid-main': { overflow: 'auto' },
            },
          }}
          noResultsMessage={
            hasActiveFilters
              ? t(
                  'Communities.CommunityInvitesTable.NoResults',
                  'No invites found'
                )
              : t(
                  'Communities.CommunityInvitesTable.Empty',
                  'No invites available'
                )
          }
          pagination={{
            ...pagination,
            page: pagination.page - 1,
          }}
          handlePaginationChange={handlePaginationChange}
          onCellClick={(params) => {
            const { row, field } = params
            handleCellClick(row as CommunityInviteRow, field)
          }}
        />
      )}
    </section>
  )
}

export default CommunityInvitesTable
